import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import {
  always,
  compose,
  cond,
  either,
  prop,
  propOr,
  T,
  test
} from 'ramda'
import { Helmet } from 'react-helmet'
import classnames from 'classnames'
import { useSelector } from 'react-redux'

import ComponentsList from '../components'
import AsyncBackToTop from '../shared/back-to-top'
import SCREEN_FRAGMENT from '../../../graphql/fragments/screen.gql'
import { SCREEN_SIZE_BREAKPOINTS } from '../../constants'
import { getLocationBaseWithHref } from '../../lib/location'

import styles from './screen.css'

export const getNeonScreenMeta = compose(cond([
  [either(test(/^\/tv$/), test(/^\/tv-series$/)), always({
    title: 'Watch your favourite TV Shows Online with NEON',
    description: 'Get unlimited access to a huge range of epic TV shows.'
  })],
  [test(/^\/movies$/), always({
    title: 'Watch Movies Online and on the go with NEON',
    description: 'Join NEON today and get unlimited access to a huge catalogue of movies of all genres.'
  })],
  [test(/^\/rental$/), always({
    title: 'Rent Movies Online with NEON - No subscription required',
    description: 'Rent movies of all genres online with NEON, starting as low as $4.99 per movie. No subscription required.'
  })],
  [test(/^\/kids$/), always({
    title: 'Watch your favourite Kids Shows & Movies online with NEON',
    description: 'Get unlimited access to a huge range of kids’ shows.'
  })],
  [test(/^\/about-lightbox$/), always({
    title: 'About NEON',
    description: 'Watch hit TV shows on-demand and rent blockbuster movies with NEON.'
  })],
  [test(/^\/about-neon$/), always({
    title: 'About NEON',
    description: 'Watch hit TV shows on-demand and rent blockbuster movies with NEON.'
  })],
  [test(/^\/my-lists$/), always({
    title: 'My lists - NEON'
  })],
  [test(/^\/my-rentals$/), always({
    title: 'My rentals - NEON'
  })],
  [test(/^\/max$/), always({
    title: 'Watch MAX TV shows, series and movies. Stream MAX now on NEON',
    description: 'NEON offers a diverse and exciting selection of MAX movies and shows, bringing you the latest releases and exclusive content. Whether you’re in the mood for high quality dramas, side-splitting comedies, or captivating documentaries, MAX on NEON has something for everyone. Stream your favourites anytime, anywhere, and immerse yourself in the ultimate entertainment experience.'
  })],
  [T, always({})]
]))

export const screenHasMeta = compose(Boolean, prop('title'), getNeonScreenMeta)

const ScreenView = ({
  header,
  data,
  isContentItemOnMyList,
  addToMyList,
  removeFromMyList,
  playbackInfoMany,
  location,
  isMarketing,
  screenTitle,
  screenWidth,
  refetch
}) => {
  const mapState = useSelector(state => state)
  const { theme } = mapState
  const mounted = useRef()

  const screen = propOr({}, 'screen', data)
  const components = propOr([], 'components', screen)
  /*
  ** The CategoryComponent is always rendered together with the Grid component,
  ** both of them include their own BackToTop button.
  ** So hide the button when component is CategoryComponent.
  */
  const hideBackToTopButton = components[0]?.__typename === 'CategoryComponent'
  const marketComponent = prop('marketComponent', data)

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else if (data && !screen) {
      // ComponentDidUpdate
      refetch()
    }
  }, [refetch, screen])

  const children = [(
    <div
      key={screen.id}
      className={classnames(styles.childrenFrame, {
        [styles.blackNav]: isMarketing
      })}
    >
      {
        header &&
        <h1 className={styles.header}>{header}</h1>
      }
      <ComponentsList
        components={components}
        marketComponent={marketComponent}
        isContentItemOnMyList={isContentItemOnMyList}
        theme={theme}
        addToMyList={addToMyList}
        removeFromMyList={removeFromMyList}
        playbackInfoMany={playbackInfoMany}
        screenTitle={screenTitle}
        isMarketing={isMarketing}
      />
      {
        screenWidth < SCREEN_SIZE_BREAKPOINTS.MIN_480 && !hideBackToTopButton && <AsyncBackToTop />
      }
    </div>
  )]

  // add noindex, nofollow to prevent search engine crawling and indexing for specific path
  const pathsNoIndex = ['/2025-july-cart-drop-voucher-campaign']
  const noindexMetaTag = pathsNoIndex.includes(data.screen.path)
    ? (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    )
    : null

  if (!screenHasMeta(location.pathname)) {
    return [noindexMetaTag, ...children]
  }
  const { title, description } = getNeonScreenMeta(location.pathname)

  return [
    <Helmet key={title + description}>
      <meta property="og:url" content={getLocationBaseWithHref()} />
      {description ? (
        <meta
          name="description"
          property="og:description"
          content={description}
        />
      ) : null}
      <meta name="title" property="og:title" content={title} />
      <title>{title}</title>
    </Helmet>,
    noindexMetaTag,
    ...children
  ]
}

ScreenView.propTypes = {
  data: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.object]),
    screen: propType(SCREEN_FRAGMENT)
  }).isRequired,
  addToMyList: PropTypes.func,
  removeFromMyList: PropTypes.func,
  isContentItemOnMyList: PropTypes.func.isRequired,
  header: PropTypes.string,
  playbackInfoMany: PropTypes.oneOfType([PropTypes.object]),
  isMarketing: PropTypes.bool.isRequired,
  screenWidth: PropTypes.number.isRequired,
  screenTitle: PropTypes.string,
  refetch: PropTypes.func.isRequired
}

ScreenView.defaultProps = {
  header: '',
  addToMyList: T,
  removeFromMyList: T,
  playbackInfoMany: null,
  screenTitle: ''
}

export default ScreenView
